import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, DataTable, StatusBadge, RowActions, Pagination,
  FilterBar, FilterField, RadixSelect, type DataTableColumn, type RowActionItem,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '@/components/ui';
import { clampPage, pageSlice } from '../lib/listPaging';
import { useEverTvVideosQuery, useEverTvVideoMutation } from '../hooks/useEverTvVideosQuery';
import { EVER_TV_CATEGORIES, type EverTvVideo } from '../types/everTvVideo';

const ALL_CATEGORY = '';

/**
 * 한 화면에 보여줄 행 수.
 *
 * BE 관리자 목록이 Spring Page 가 아니라 plain array 라 서버 페이징이 없다. 전량을 받아
 * 클라이언트에서 자른다 — 영상이 쌓일수록 한 화면에 수백 행을 렌더하던 것을 막는 게 목적이고,
 * 카테고리 필터도 이미 클라이언트에서 걸고 있어 같은 자리에 붙인다(홈 팝업 목록과 같은 방식).
 */
const PAGE_SIZE = 20;

const formatPublishedAt = (value: string | null): string =>
  value && value.trim() ? value.trim() : '-';

export const EverTvListPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [category, setCategory] = useState(ALL_CATEGORY);
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<EverTvVideo | null>(null);

  const query = useEverTvVideosQuery();
  const { remove } = useEverTvVideoMutation();

  // 관리자 목록은 plain array — 카테고리 필터는 클라이언트 측에서 적용한다.
  const items = useMemo(
    () => (category ? (query.data ?? []).filter((v) => v.category === category) : query.data ?? []),
    [query.data, category],
  );

  // 필터가 바뀌면 보던 페이지 번호는 의미가 없다 — 3페이지를 보다 필터를 걸면 빈 화면이 된다.
  useEffect(() => setPage(1), [category]);

  // 목록이 줄어들면(필터·삭제) 보던 페이지가 범위 밖으로 남는다 — 자르기 전에 좁힌다.
  // 좁히지 않으면 데이터는 있는데 표만 비는 화면이 된다.
  const safePage = clampPage(page, items.length, PAGE_SIZE);
  const pagedItems = useMemo(() => pageSlice(items, safePage, PAGE_SIZE), [items, safePage]);

  const categoryOptions = useMemo(
    () => [
      { value: ALL_CATEGORY, label: t('everTv.filter.categoryAll') },
      ...EVER_TV_CATEGORIES.map((c) => ({ value: c, label: t(`everTv.category.${c}`) })),
    ],
    [t],
  );

  const columns: DataTableColumn<EverTvVideo>[] = [
    {
      key: 'title',
      header: t('everTv.cols.title'),
      cell: (row) => (
        <span
          className="block max-w-[360px] truncate text-sm font-medium"
          style={{ color: 'var(--content-strong)' }}
          title={row.title}
        >
          {row.title}
        </span>
      ),
    },
    {
      key: 'category',
      header: t('everTv.cols.category'),
      width: 120,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {row.category ? t(`everTv.category.${row.category}`) : '-'}
        </span>
      ),
    },
    {
      key: 'sortOrder',
      header: t('everTv.cols.sortOrder'),
      align: 'right',
      width: 80,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.sortOrder}
        </span>
      ),
    },
    {
      key: 'isActive',
      header: t('everTv.cols.isActive'),
      align: 'center',
      width: 96,
      cell: (row) => (
        <StatusBadge
          variant={row.isActive ? 'success' : 'neutral'}
          label={row.isActive ? t('everTv.active') : t('everTv.inactive')}
        />
      ),
    },
    {
      key: 'publishedAt',
      header: t('everTv.cols.publishedAt'),
      width: 180,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatPublishedAt(row.publishedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 56,
      cell: (row) => {
        const actions: RowActionItem[] = [
          { key: 'edit', label: t('everTv.edit'), onClick: () => navigate(`/cms/ever-tv/${row.videoId}`) },
          {
            key: 'delete',
            label: t('everTv.delete'),
            variant: 'danger' as const,
            separatorBefore: true,
            onClick: () => setDeleteTarget(row),
          },
        ];
        return <RowActions triggerLabel={t('everTv.rowActionsLabel')} actions={actions} />;
      },
    },
  ];

  return (
    <PageShell
      title={t('everTv.title')}
      actions={
        <Link to="/cms/ever-tv/new">
          <Button size="sm">
            <Plus size={15} aria-hidden="true" />
            {t('everTv.create')}
          </Button>
        </Link>
      }
    >
      <FilterBar
        className="mb-4"
        filters={
          <FilterField label={t('everTv.filter.category')}>
            <RadixSelect
              value={category}
              onChange={setCategory}
              options={categoryOptions}
              aria-label={t('everTv.filter.category')}
            />
          </FilterField>
        }
        actions={
          <Button size="sm" variant="outline" onClick={() => setCategory(ALL_CATEGORY)}>
            {t('everTv.filter.reset')}
          </Button>
        }
      />

      {query.isPending ? (
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-[44px] w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          aria-live="polite"
          className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
        >
          <p>{t('everTv.loadFailed')}</p>
          <Button variant="outline" size="sm" onClick={() => query.refetch()}>
            {t('everTv.retry')}
          </Button>
        </div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={pagedItems}
            rowKey={(row) => row.videoId}
            emptyState={t('everTv.empty')}
            stickyHeader
          />

          {items.length > 0 ? (
            <Pagination
              total={items.length}
              currentPage={safePage}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              className="mt-4"
            />
          ) : null}
        </>
      )}

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('everTv.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('everTv.deleteDesc', { title: deleteTarget?.title ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('everTv.cancel')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() => {
                if (deleteTarget) remove.mutate(deleteTarget.videoId, { onSuccess: () => setDeleteTarget(null) });
              }}
            >
              {t('everTv.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
